import { useParams } from 'react-router-dom'
import { TaskRenderer, type RenderableItem } from '@sciolingo/renderer'
import { explanationSchema } from '@sciolingo/api-client'
import type { CachedItem, LocalResponse } from '../../db/types'
import { formatRemaining } from '../../offline/clock'
import styles from './SessionRunner.module.css'
import { useSessionRunner } from './useSessionRunner'

/**
 * The session runner: the shared shell that hosts the task renderer, the
 * progress indicator and navigation between items.
 *
 * The shell and the renderer are kept apart — everything on this screen that is
 * *not* the item itself lives here (its logic in `useSessionRunner`), and the
 * renderer receives an item and emits answers, with no navigation, timer, fetch
 * or persistence of its own. It is handed no correct answer and no explanation
 * (§9 rule 1); the runner evaluates and this screen shows the explanation.
 */
export function SessionRunner() {
  const { sessionId = '' } = useParams()
  const {
    session,
    items,
    index,
    item,
    answered,
    answeredCount,
    isLast,
    remaining,
    draft,
    setDraft,
    busy,
    goTo,
    check,
    finish,
  } = useSessionRunner(sessionId)

  if (!session) return <p className="muted">Načítám sezení…</p>
  if (items.length === 0) return <p className="muted">Toto sezení neobsahuje žádné zobrazitelné úlohy.</p>
  if (!item) return <p className="muted">Úloha není k dispozici.</p>

  const renderable: RenderableItem = {
    id: item.id,
    version: item.version,
    type: item.type,
    content: item.content,
    // Assets are resolved by the caller, never fetched by the renderer (§9 rule 2).
    assets: new Map(),
  }

  return (
    <div className="stack">
      <header className={styles.head}>
        <div className={styles.progress}>
          <span>
            Úloha {index + 1} z {items.length}
          </span>
          <progress value={answeredCount} max={items.length} />
        </div>

        {remaining.remainingMs !== null ? (
          <span className={`pill ${remaining.remainingMs < 60_000 ? styles.urgent : ''}`} role="timer">
            {formatRemaining(remaining.remainingMs)}
          </span>
        ) : null}
      </header>

      <article className="card">
        <TaskRenderer
          item={renderable}
          initialAnswer={answered?.answer}
          disabled={answered !== undefined || busy}
          onAnswerChange={setDraft}
          onInteraction={() => {
            /* telemetry sink — not wired yet; intended for the diagnostics counters */
          }}
        />
      </article>

      {answered ? <Feedback response={answered} item={item} /> : null}

      <footer className={styles.foot}>
        <button className="button" type="button" disabled={index === 0} onClick={() => goTo(index - 1)}>
          Zpět
        </button>

        {answered ? (
          isLast ? (
            <button className="button button--primary" type="button" disabled={busy} onClick={() => void finish()}>
              Dokončit
            </button>
          ) : (
            <button className="button button--primary" type="button" onClick={() => goTo(index + 1)}>
              Další úloha
            </button>
          )
        ) : (
          <button
            className="button button--primary"
            type="button"
            disabled={draft === undefined || busy}
            onClick={() => void check()}
          >
            Zkontrolovat
          </button>
        )}
      </footer>
    </div>
  )
}

/**
 * §5.3 and §2 decision 2: per-item correct/incorrect and the explanation, both
 * available offline. What is absent here is deliberate — no percentile, no plan
 * update, and the verdict is labelled provisional until the server has spoken:
 * local evaluation never stands in for the server result.
 */
function Feedback({ response, item }: { response: LocalResponse; item: CachedItem }) {
  const explanation = explanationSchema.safeParse(item.explanation)
  const verdictClass =
    response.localResult === 'correct' ? styles.correct : response.localResult === 'incorrect' ? styles.incorrect : ''

  return (
    <section className={`card ${styles.feedback} ${verdictClass}`}>
      <h2 className="card__title">
        {response.localResult === 'correct' ? 'Správně' : response.localResult === 'incorrect' ? 'Chyba' : 'Nelze vyhodnotit'}
        {response.syncState !== 'synced' ? <span className="tag">předběžné</span> : null}
      </h2>

      {explanation.success ? (
        <>
          <p>{explanation.data.text}</p>
          {explanation.data.steps ? (
            <ol className={styles.steps}>
              {explanation.data.steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          ) : null}
        </>
      ) : (
        <p className="muted">K této úloze zatím není vysvětlení.</p>
      )}
    </section>
  )
}
